import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import ExecutetickIconLight from "../../../images/orders/execute.svg";
import ExecutetickIconDark from "../../../images/orders/tick-circle.svg";
import { Info } from "lucide-react";
import { Tooltip as ReactTooltip } from "react-tooltip";

function formatNumberWithFontWeight(number) {
  if (number === "Unlimited") {
    return <span style={{ fontWeight: 500 }}>Unlimited</span>;
  }

  // Convert to number if it's a string
  const numValue = typeof number === 'string' ? parseFloat(number) : number;

  // Check for invalid numbers, including Infinity and -Infinity
  if (isNaN(numValue) || !isFinite(numValue)) {
    return <span>-</span>;
  }

  const formattedValue = numValue.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const parts = formattedValue.split(".");
  const formattedFraction = <span style={{ fontWeight: 400 }}>{parts[1]}</span>;

  return (
    <span>
      {parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")}.{""}
      {formattedFraction}
    </span>
  );
}
const Divider = () => <div className="strategy-divider" />;
const OverallCard = ({
  netProfit,
  pendingProfit,
  achievedPercent,
  potentialLoss,
  potentialProfit,
  fundsData,
  utilisedPercentage,
}) => {
  const { theme } = useTheme();
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [showTooltip1, setShowTooltip1] = useState(false);
  return (
    <div className="flex flex-col gap-3 min-w-[500px]">
      {/* Position Analytics */}
      <div className="bg-p400 shadow-md rounded-md w-full overflow-hidden">
        {/* Title */}
        <div className="p-4">
          <p className="text-p300 text-sm font-semibold">All Positions</p>
        </div>

        {/* Top 3 Sections */}
        <div className=" border-gray-200 flex flex-row justify-between items-start  pt-0 gap-4 px-4 mb-3">
          {/* Potential Profit */}
          <div className="strategy-menu-item flex flex-col">
            <div className="creator-bottom-title inline-flex items-center relative"> Potential Profit
              <div
                className="ml-1"
                onMouseEnter={(e) => {
                  setTooltipPos({ x: e.clientX + 12, y: e.clientY + 12 });
                  setShowTooltip1(true);
                }}
                onMouseLeave={() => setShowTooltip1(false)}>
                <Info size={14} className="text-gray-400 cursor-pointer" />
              </div>
              {showTooltip1 && (
                <div
                  className="fixed z-50 bg-n600 text-p400 text-xs rounded px-3 py-2 shadow-lg max-w-xs pointer-events-none"
                  style={{ top: tooltipPos.y, left: tooltipPos.x }}>
                  Shows how much of profit can be achieved yet.
                </div>
              )}
            </div>
            <span className="creator-bottom-value">
              {formatNumberWithFontWeight(potentialProfit)}
            </span>
          </div>
          <Divider />
          <div className="strategy-menu-item flex flex-col">
            <span className="creator-bottom-title"> Potential Loss</span>
            <span className="creator-bottom-value">
              {formatNumberWithFontWeight(potentialLoss)}
            </span>
          </div>
          <Divider />
          <div className="strategy-menu-item flex flex-col">
            <span className="creator-bottom-title inline-flex items-center relative">
              Achieved P&L %
              <div
                className="ml-1"
                onMouseEnter={(e) => {
                  setTooltipPos({ x: e.clientX + 12, y: e.clientY + 12 });
                  setShowTooltip(true);
                }}
                onMouseLeave={() => setShowTooltip(false)}>
                <Info size={14} className="text-gray-400 cursor-pointer" />
              </div>
              {showTooltip && (
                <div
                  className="fixed z-50 bg-n600 text-p400 text-xs rounded px-3 py-2 shadow-lg max-w-xs pointer-events-none"
                  style={{ top: tooltipPos.y, left: tooltipPos.x }}>
                  Shows how much of profit is achieved of the potential profit, cannot be computed if potential profit is Unlimited!
                </div>
              )}
            </span>

            <span className="creator-bottom-value">
              {formatNumberWithFontWeight(achievedPercent)}
            </span>
          </div>
        </div>

        {/* Overall P&L Section */}
        <div className="flex items-center bg-opacity-80 bg-n000 justify-between p-3 gap-2 border-t mt-2 px-4">
          <div className="flex items-center gap-2">
            <span className="w-1 h-1 rounded-full bg-p300"></span>
            <p className="text-p300 text-sm font-medium">Overall P&L</p>
            <span className="text-n500 font-medium text-xs">
              (Analytics of all position combined)
            </span>
          </div>
          <p
            className={`
          font-medium text-sm 
          ${netProfit > 0 ? "text-p150" : "text-red"}
        `}>
            {formatNumberWithFontWeight(netProfit)}
          </p>
        </div>
      </div>

      {/* Funds Section */}
      <div className="bg-p400 rounded-md shadow-md p-4 flex items-center justify-between gap-6 w-full mt-4">
        {/* Left Section: Margin & Funds Title */}
        <div className="flex flex-col  w-full">
          <p className="text-p300 text-sm font-semibold">Margin & Funds</p>
          <div className="flex justify-between items-center mt-4">
            <div className="strategy-menu-item flex flex-col">
              <span className="creator-bottom-title">Available Fund</span>
              <span className="creator-bottom-value">
                ₹ {formatNumberWithFontWeight(fundsData?.marginAvailable)}
              </span>
            </div>
            <Divider />
            <div className="strategy-menu-item flex flex-col">
              <span className="creator-bottom-title">Utilised Fund</span>
              <span className="creator-bottom-value">
                ₹ {formatNumberWithFontWeight(fundsData?.marginUtilized)}
              </span>
            </div>
            <div>
              <Link
                to={fundsData?.payment_url}
                target="_blank"
                className="text-decoration-none">
                <button className="bg-p300 text-p400 h-9 w-[117px] rounded-md flex items-center justify-center gap-2 font-semibold text-sm ml-auto">
                  {theme === "dark" ? (
                    <img src={ExecutetickIconDark} alt="" />
                  ) : (
                    <img src={ExecutetickIconLight} alt="" />
                  )}
                  <span>Add Funds</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverallCard;